/**
 * useReportingDashboard
 *
 * Derives all dashboard metrics from the reporting_entries cache in a single useMemo pass.
 * No API calls — everything is computed client-side from cached data.
 */

import { startOfWeek as dfStartOfWeek } from "date-fns";
import { useMemo } from "react";
import {
	type BillableCategory,
	type BillingCategory,
	type ReportingEntry,
	useMattersCache,
	useReportingEntriesCache,
	useReportsCache,
} from "@/shared/cache";
import { countWeekdays } from "@/shared/utils/workdays";
import { getReportingEntryStatus } from "./reportingStatus";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export interface DashboardStats {
	totalHours: number;
	exportedHours: number;
	trackedHours: number;
	deletedHours: number;
	entryCount: number;
	matterCount: number;
	avgPerDay: number;
}

export interface DailyChartPoint {
	date: string; // YYYY-MM-DD
	dateLabel: string; // "4/7"
	tooltipLabel: string; // "Monday 4/7"
	exported: number;
	tracked: number;
	deleted: number;
}

export interface MemberRow {
	profId: string;
	profName: string;
	profType: string | null;
	tracked: number;
	exported: number;
	deleted: number;
	total: number;
	entries: number;
	matters: number;
	avgPerDay: number;
}

export interface ClientRow {
	client: string;
	hours: number;
	exported: number;
	tracked: number;
	deleted: number;
	entries: number;
}

export interface MatterRow {
	matterId: string;
	matterNumber: string;
	matterDescription: string;
	client: string;
	hours: number;
	entries: number;
	billingCategory: BillingCategory;
	billableCategory: BillableCategory;
}

export interface CategoryBucket {
	hours: number;
	exported: number;
	tracked: number;
	deleted: number;
	entries: number;
}

export interface DashboardFilters {
	dateRange: { from: Date; to: Date };
	/** null = all entries, "me" = user's own, "team" = direct reports only, prof_id = specific person */
	scope: string | null;
	userProfId: string;
	statuses: readonly string[];
	client: string | null;
	matter: string | null;
}

export interface DashboardData {
	stats: DashboardStats;
	filteredEntries: ReportingEntry[];
	dailyChart: DailyChartPoint[];
	memberTable: MemberRow[];
	topClients: ClientRow[];
	topMatters: MatterRow[];
	hoursByBillingCategory: Partial<Record<BillingCategory, CategoryBucket>>;
	hoursByBillableCategory: Partial<Record<BillableCategory, CategoryBucket>>;
	availableClients: string[];
	availableMatters: {
		matterId: string;
		matterNumber: string;
		matterDescription: string;
		client: string;
	}[];
	isLoading: boolean;
}

// ─────────────────────────────────────────────
// Date Range Presets
// ─────────────────────────────────────────────

export type DatePreset =
	| "today"
	| "last-7"
	| "this-week"
	| "last-week"
	| "this-month"
	| "last-month"
	| "last-30"
	| "this-quarter"
	| "last-quarter"
	| "this-year";

export function getDateRange(preset: DatePreset): { from: Date; to: Date } {
	const today = new Date();
	today.setHours(0, 0, 0, 0);

	// Picks up firm-configured weekStartsOn via date-fns setDefaultOptions in main.tsx
	const startOfWeek = (d: Date) => {
		const s = dfStartOfWeek(d);
		s.setHours(0, 0, 0, 0);
		return s;
	};

	switch (preset) {
		case "today":
			return { from: today, to: today };
		case "last-7": {
			const from = new Date(today);
			from.setDate(from.getDate() - 7);
			return { from, to: today };
		}
		case "this-week": {
			return { from: startOfWeek(today), to: today };
		}
		case "last-week": {
			const end = new Date(startOfWeek(today));
			end.setDate(end.getDate() - 1);
			return { from: startOfWeek(end), to: end };
		}
		case "this-month": {
			return {
				from: new Date(today.getFullYear(), today.getMonth(), 1),
				to: today,
			};
		}
		case "last-month": {
			const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
			const to = new Date(today.getFullYear(), today.getMonth(), 0);
			return { from, to };
		}
		case "last-30": {
			const from = new Date(today);
			from.setDate(from.getDate() - 30);
			return { from, to: today };
		}
		case "this-quarter": {
			const q = Math.floor(today.getMonth() / 3);
			return { from: new Date(today.getFullYear(), q * 3, 1), to: today };
		}
		case "last-quarter": {
			const q = Math.floor(today.getMonth() / 3);
			const from = new Date(today.getFullYear(), (q - 1) * 3, 1);
			const to = new Date(today.getFullYear(), q * 3, 0);
			return { from, to };
		}
		case "this-year": {
			return { from: new Date(today.getFullYear(), 0, 1), to: today };
		}
	}
}

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
	today: "Today",
	"last-7": "Last 7 Days",
	"this-week": "This Week",
	"last-week": "Last Week",
	"this-month": "This Month",
	"last-month": "Last Month",
	"last-30": "Last 30 Days",
	"this-quarter": "This Quarter",
	"last-quarter": "Last Quarter",
	"this-year": "Year to Date",
};

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function parseEntryDate(dateStr: string): Date {
	const part = dateStr.split("T")[0];
	const [y, m, d] = part.split("-").map(Number);
	return new Date(y, m - 1, d);
}

function toDateKey(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

function formatDateLabel(dateKey: string): string {
	const [, m, d] = dateKey.split("-").map(Number);
	return `${m}/${d}`;
}

function formatTooltipLabel(dateKey: string): string {
	const [y, m, d] = dateKey.split("-").map(Number);
	const date = new Date(y, m - 1, d);
	const days = [
		"Sunday",
		"Monday",
		"Tuesday",
		"Wednesday",
		"Thursday",
		"Friday",
		"Saturday",
	];
	return `${days[date.getDay()]} ${m}/${d}`;
}

// ─────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────

export function useReportingDashboard(
	filters: DashboardFilters,
): DashboardData {
	const {
		allEntries,
		userProfId: _userProfId,
		isLoading,
	} = useReportingEntriesCache();
	const { reports } = useReportsCache();
	const { matters } = useMattersCache();

	return useMemo(() => {
		// matter_id → categories lookup. Falls back to 'unknown' when a matter is
		// missing from the cache (e.g. cache hasn't loaded, or matter was deleted).
		const categoriesByMatterId = new Map<
			string,
			{ billing: BillingCategory; billable: BillableCategory }
		>();
		for (const m of matters) {
			categoriesByMatterId.set(m.matter_id, {
				billing: m.billing_category,
				billable: m.billable_category,
			});
		}
		const categoriesFor = (
			matterId: string | null,
		): { billing: BillingCategory; billable: BillableCategory } =>
			(matterId && categoriesByMatterId.get(matterId)) || {
				billing: "unknown",
				billable: "unknown",
			};

		// Collect available clients from ALL entries (unfiltered) so dropdown doesn't collapse
		const clientSet = new Set<string>();
		// Collect available matters scoped to selected client (or all if no client filter)
		const matterInfoMap = new Map<
			string,
			{
				matterId: string;
				matterNumber: string;
				matterDescription: string;
				client: string;
			}
		>();
		for (const e of allEntries) {
			if (e.client) clientSet.add(e.client);
			if (e.matter_id && !matterInfoMap.has(e.matter_id)) {
				// Only include matters matching client filter (for the dropdown)
				if (!filters.client || e.client === filters.client) {
					matterInfoMap.set(e.matter_id, {
						matterId: e.matter_id,
						matterNumber: e.matter_number || "",
						matterDescription: e.matter_description || "",
						client: e.client || "Unknown",
					});
				}
			}
		}

		// Filter entries
		const selectedStatuses = new Set(filters.statuses);
		const filtered: ReportingEntry[] = [];
		for (const e of allEntries) {
			// Date filter
			const entryDate = parseEntryDate(e.date);
			if (
				entryDate < filters.dateRange.from ||
				entryDate > filters.dateRange.to
			)
				continue;

			// Scope filter
			if (filters.scope === "me" && e.prof_id !== filters.userProfId) continue;
			if (filters.scope === "team" && e.prof_id === filters.userProfId)
				continue;
			if (
				filters.scope &&
				filters.scope !== "me" &&
				filters.scope !== "team" &&
				e.prof_id !== filters.scope
			)
				continue;

			// Status filter
			if (!selectedStatuses.has(getReportingEntryStatus(e))) continue;

			// Client filter
			if (filters.client && e.client !== filters.client) continue;

			// Matter filter
			if (filters.matter && e.matter_id !== filters.matter) continue;

			filtered.push(e);
		}

		// Single-pass aggregation
		let totalHours = 0;
		let exportedHours = 0;
		let trackedHours = 0;
		let deletedHours = 0;
		const matterIds = new Set<string>();
		const dailyMap = new Map<
			string,
			{ exported: number; tracked: number; deleted: number }
		>();
		const memberMap = new Map<
			string,
			{
				profName: string;
				profType: string | null;
				tracked: number;
				exported: number;
				deleted: number;
				entries: number;
				matters: Set<string>;
				days: Set<string>;
			}
		>();
		const clientMap = new Map<
			string,
			{
				hours: number;
				exported: number;
				tracked: number;
				deleted: number;
				entries: number;
			}
		>();
		const matterMap = new Map<
			string,
			{
				matterNumber: string;
				matterDescription: string;
				client: string;
				hours: number;
				entries: number;
				billingCategory: BillingCategory;
				billableCategory: BillableCategory;
			}
		>();
		const billingMap = new Map<BillingCategory, CategoryBucket>();
		const billableMap = new Map<BillableCategory, CategoryBucket>();
		const bumpBucket = <K extends string>(
			map: Map<K, CategoryBucket>,
			key: K,
			units: number,
			status: "tracked" | "reviewed" | "exported" | "deleted",
		) => {
			let b = map.get(key);
			if (!b) {
				b = { hours: 0, exported: 0, tracked: 0, deleted: 0, entries: 0 };
				map.set(key, b);
			}
			b.hours += units;
			if (status === "exported") b.exported += units;
			else if (status === "deleted") b.deleted += units;
			else b.tracked += units;
			b.entries++;
		};

		for (const e of filtered) {
			const units = e.units;
			const status = getReportingEntryStatus(e);
			totalHours += units;
			if (status === "exported") exportedHours += units;
			else if (status === "deleted") deletedHours += units;
			else trackedHours += units;

			if (e.matter_id) matterIds.add(e.matter_id);

			// Daily chart
			const dateKey = toDateKey(parseEntryDate(e.date));
			const daily = dailyMap.get(dateKey) || {
				exported: 0,
				tracked: 0,
				deleted: 0,
			};
			if (status === "exported") daily.exported += units;
			else if (status === "deleted") daily.deleted += units;
			else daily.tracked += units;
			dailyMap.set(dateKey, daily);

			// Member table
			let member = memberMap.get(e.prof_id);
			if (!member) {
				const person = reports.manages.find((p) => p.prof_id === e.prof_id);
				member = {
					profName: e.prof_name || person?.full_name || e.prof_id,
					profType: person?.prof_type || null,
					tracked: 0,
					exported: 0,
					deleted: 0,
					entries: 0,
					matters: new Set(),
					days: new Set(),
				};
				memberMap.set(e.prof_id, member);
			}
			if (status === "exported") member.exported += units;
			else if (status === "deleted") member.deleted += units;
			else member.tracked += units;
			member.entries++;
			if (e.matter_id) member.matters.add(e.matter_id);
			member.days.add(dateKey);

			// Client breakdown
			const clientName = e.client || "Unknown";
			let cl = clientMap.get(clientName);
			if (!cl) {
				cl = { hours: 0, exported: 0, tracked: 0, deleted: 0, entries: 0 };
				clientMap.set(clientName, cl);
			}
			cl.hours += units;
			if (status === "exported") cl.exported += units;
			else if (status === "deleted") cl.deleted += units;
			else cl.tracked += units;
			cl.entries++;

			// Matter breakdown + per-category buckets
			const cats = categoriesFor(e.matter_id);
			bumpBucket(billingMap, cats.billing, units, status);
			bumpBucket(billableMap, cats.billable, units, status);

			if (e.matter_id) {
				let mt = matterMap.get(e.matter_id);
				if (!mt) {
					mt = {
						matterNumber: e.matter_number || "",
						matterDescription: e.matter_description || "",
						client: e.client || "Unknown",
						hours: 0,
						entries: 0,
						billingCategory: cats.billing,
						billableCategory: cats.billable,
					};
					matterMap.set(e.matter_id, mt);
				}
				mt.hours += units;
				mt.entries++;
			}
		}

		// Build outputs
		const weekdays =
			countWeekdays(filters.dateRange.from, filters.dateRange.to) || 1;

		const stats: DashboardStats = {
			totalHours: Math.round(totalHours * 10) / 10,
			exportedHours: Math.round(exportedHours * 10) / 10,
			trackedHours: Math.round(trackedHours * 10) / 10,
			deletedHours: Math.round(deletedHours * 10) / 10,
			entryCount: filtered.length,
			matterCount: matterIds.size,
			avgPerDay: Math.round((totalHours / weekdays) * 10) / 10,
		};

		// Daily chart: fill in missing dates in range
		const dailyChart: DailyChartPoint[] = [];
		const d = new Date(filters.dateRange.from);
		while (d <= filters.dateRange.to) {
			const key = toDateKey(d);
			const val = dailyMap.get(key) || { exported: 0, tracked: 0, deleted: 0 };
			dailyChart.push({
				date: key,
				dateLabel: formatDateLabel(key),
				tooltipLabel: formatTooltipLabel(key),
				exported: Math.round(val.exported * 10) / 10,
				tracked: Math.round(val.tracked * 10) / 10,
				deleted: Math.round(val.deleted * 10) / 10,
			});
			d.setDate(d.getDate() + 1);
		}

		const memberTable: MemberRow[] = Array.from(memberMap.entries())
			.map(([profId, m]) => ({
				profId,
				profName: m.profName,
				profType: m.profType,
				tracked: Math.round(m.tracked * 10) / 10,
				exported: Math.round(m.exported * 10) / 10,
				deleted: Math.round(m.deleted * 10) / 10,
				total: Math.round((m.tracked + m.exported + m.deleted) * 10) / 10,
				entries: m.entries,
				matters: m.matters.size,
				avgPerDay:
					Math.round(
						((m.tracked + m.exported + m.deleted) / (m.days.size || 1)) * 10,
					) / 10,
			}))
			.sort((a, b) => b.total - a.total);

		const topClients: ClientRow[] = Array.from(clientMap.entries())
			.map(([client, c]) => ({
				client,
				hours: Math.round(c.hours * 10) / 10,
				exported: Math.round(c.exported * 10) / 10,
				tracked: Math.round(c.tracked * 10) / 10,
				deleted: Math.round(c.deleted * 10) / 10,
				entries: c.entries,
			}))
			.sort((a, b) => b.hours - a.hours);

		const topMatters: MatterRow[] = Array.from(matterMap.entries())
			.map(([matterId, m]) => ({
				matterId,
				matterNumber: m.matterNumber,
				matterDescription: m.matterDescription,
				client: m.client,
				hours: Math.round(m.hours * 10) / 10,
				entries: m.entries,
				billingCategory: m.billingCategory,
				billableCategory: m.billableCategory,
			}))
			.sort((a, b) => b.hours - a.hours);

		const roundBucket = (b: CategoryBucket): CategoryBucket => ({
			hours: Math.round(b.hours * 10) / 10,
			exported: Math.round(b.exported * 10) / 10,
			tracked: Math.round(b.tracked * 10) / 10,
			deleted: Math.round(b.deleted * 10) / 10,
			entries: b.entries,
		});
		const hoursByBillingCategory: Partial<
			Record<BillingCategory, CategoryBucket>
		> = {};
		for (const [k, v] of billingMap) hoursByBillingCategory[k] = roundBucket(v);
		const hoursByBillableCategory: Partial<
			Record<BillableCategory, CategoryBucket>
		> = {};
		for (const [k, v] of billableMap)
			hoursByBillableCategory[k] = roundBucket(v);

		return {
			stats,
			filteredEntries: filtered,
			dailyChart,
			memberTable,
			topClients,
			topMatters,
			hoursByBillingCategory,
			hoursByBillableCategory,
			availableClients: Array.from(clientSet).sort(),
			availableMatters: Array.from(matterInfoMap.values()).sort((a, b) =>
				a.matterNumber.localeCompare(b.matterNumber),
			),
			isLoading,
		};
	}, [allEntries, reports, matters, filters, isLoading]);
}
